import {Component, Element, Event, EventEmitter, h, Listen, Method, Prop, Watch} from '@stencil/core';

/**
 * @slot default - To display the list of joy-selectable-item.
 * @slot selectable-item-group-label - Use joy-label directly with this slot to display a label for the whole item group.
 */
@Component({
    tag: 'joy-selectable-item-group',
    styleUrl: 'selectable-item-group.scss',
    shadow: true,
})
export class SelectableItemGroup {
    /** Meant for multiple items selection */
    private selectedValues: string[] = [];

    @Element() host!: HTMLJoySelectableItemGroupElement;
    /** If multiple is set to true, it will display joy-checkboxes inside selectable-item children elements. If not, a hidden radio box is included **/
    @Prop({reflect: true}) multiple = false;
    /** If set to true, css flex rule will by applied in order to take all horizontal space available **/
    @Prop({reflect: true}) fullWidth = false;
    /** Selected item **/
    @Prop({mutable: true}) value?: string | string[];

    @Event({eventName: 'value-change'})
    valueChange!: EventEmitter<{value: string; checked: boolean}>;

    @Event({eventName: 'joy-selectable-item-group-change'})
    joySelectableItemGroupChange!: EventEmitter<{value: string; checked: boolean} | string[]>;

    @Watch('value')
    updateItems() {
        /** It's important here to only refres children state and not dealing with group state */
        this.checkItemsAccordingToValue();
    }
    /**
     * If multiple is true, returns an array of all checked children joy-selectable-item values
     * If multiple is false, returns value prop
     * @return {Promise}
     */
    @Method()
    async getSelectedItemsValue() {
        if (!this.multiple) {
            return this.value;
        }

        return this.formatMultipleSelectionValue();
    }

    private getItems() {
        return Array.from(this.host.querySelectorAll('joy-selectable-item'));
    }

    @Listen('joy-selectable-item-change')
    unselectOtherItems(e: CustomEvent & {target: HTMLJoySelectableItemElement}) {
        if (!this.host.contains(e.target)) {
            return;
        }

        let eventDetail = e.detail;

        if (!this.multiple) {
            this.value = e.detail.checked ? e.detail.value : null;

            this.getItems()
                .filter((item) => item !== e.target)
                .forEach((item) => {
                    item.checked = false;
                });
        } else {
            eventDetail = this.formatMultipleSelectionValue();
            this.value = eventDetail;
        }

        this.joySelectableItemGroupChange.emit(eventDetail);
    }

    private formatMultipleSelectionValue() {
        this.selectedValues = this.getItems()
            .filter((item: HTMLJoySelectableItemElement) => item.checked)
            .map((item) => item.value);

        return this.selectedValues;
    }

    private checkItemsAccordingToValue() {
        this.getItems().map((item) => {
            if (this.multiple) {
                item.checked = this.value ? this.value.includes(item.value.toString()) : false;
            } else {
                item.checked = this.value ? this.value === item.value.toString() : false;
            }
        });
    }

    componentWillLoad() {
        if (this.value) {
            this.checkItemsAccordingToValue();
        } else {
            this.value = this.multiple ? this.formatMultipleSelectionValue() : this.formatMultipleSelectionValue()[0];
        }
    }

    render() {
        return (
            <div>
                <slot name="selectable-item-group-label" />
                <div class="joy-selectable-item-group">
                    <slot />
                </div>
            </div>
        );
    }
}
